// CHA-622 (W-def) — Playbooks: list a tenant's workflow definitions and create / edit one.
//
// Platform and tenant-agnostic: the shell passes `call(payload)`, which runs a gateway action for its
// tenant and resolves to the callable's data. Every write goes through the gateway's
// upsert_definition — the server builds the block kit, checks the template, enforces one definition
// per rule and the admin role. No designer: a definition is just these fields.
//
// CHA-627 (W8) — To / Cc are picked from the tenant directory (list_directory): recipient people and
// groups, sent as references ({ person: id } | { group: id }), never addresses. The server resolves them
// and refuses anything it can't. No To = the legacy recipient role (during the pilot, your own inbox).
import React, { useCallback, useRef, useState } from 'react';

const EMPTY = { id: '', name: '', rule: '', template: '', subject: '', dueHours: 72, to: [], cc: [] };
const refKey = (r) => (r && r.group ? `group:${r.group}` : `person:${r && r.person}`);
const keyRef = (k) => (k.startsWith('group:') ? { group: k.slice(6) } : { person: k.slice(7) });

const inputCls = 'w-full border border-carbon-border bg-carbon-white px-2 py-1 text-[12px] text-carbon-ink';
const labelCls = 'mb-0.5 block font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-60';
const btnCls = 'font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-70 hover:text-carbon-ink disabled:opacity-40';

export default function PlaybooksPanel({ call }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(null); // null = no form; { ...fields, editing: bool }
  const [saving, setSaving] = useState(false);
  const [book, setBook] = useState({ people: [], groups: [], note: null }); // the directory, for To / Cc

  // Load the directory when a form opens. A failure leaves the pickers empty with a note — the playbook
  // can still be saved on the recipient role, but not with directory recipients (they can't be checked).
  // Every load gets a sequence number; only the LATEST may set the book, so a slow answer for a form
  // that was since cancelled or replaced never overwrites the current one (CodeRabbit on #465).
  const bookSeq = useRef(0);
  const loadBook = useCallback(async () => {
    const seq = ++bookSeq.current;
    try {
      const res = await call({ action: 'list_directory' });
      if (seq !== bookSeq.current) return;
      setBook({
        people: (Array.isArray(res?.people) ? res.people : []).filter((p) => p && p.recipient === true),
        groups: Array.isArray(res?.groups) ? res.groups : [],
        note: null,
      });
    } catch (err) {
      if (seq !== bookSeq.current) return;
      setBook({ people: [], groups: [], note: err?.message || 'Could not load the directory.' });
    }
  }, [call]);
  const openForm = (f) => { setBook({ people: [], groups: [], note: null }); setForm(f); loadBook(); };
  const closeForm = () => { bookSeq.current += 1; setForm(null); }; // drops any directory load in flight

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await call({ action: 'list_definitions' });
      setDefinitions(Array.isArray(res?.definitions) ? res.definitions : []);
      setTemplates(Array.isArray(res?.templates) ? res.templates : []);
    } catch (err) {
      setError(err?.message || 'Could not load playbooks.');
    } finally {
      setLoading(false);
    }
  }, [call]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
    else closeForm();
  };

  const edit = (d) => openForm({
    id: d.id, name: d.name || '', rule: d.rule || '', template: d.template || '', subject: d.subject || '',
    dueHours: d.dueHours ?? 72,
    to: Array.isArray(d.to) ? d.to.map(refKey) : [],
    cc: Array.isArray(d.cc) ? d.cc.map(refKey) : [],
    editing: true,
  });

  const save = async (e) => {
    e.preventDefault();
    if (!form) return;
    // Save only recipients the loaded directory can resolve (CodeRabbit on #465). An unknown one — or any,
    // when the directory didn't load — is refused here rather than sent for the server to reject.
    const known = new Set(options().filter((o) => !o.unresolved).map((o) => o.key));
    const unresolved = [...form.to, ...form.cc].filter((k) => !known.has(k));
    if (unresolved.length) {
      setError(book.note
        ? "The directory didn't load, so these recipients can't be checked: "
          + `${unresolved.map((k) => k.slice(k.indexOf(':') + 1)).join(', ')}. Try again, or untick them.`
        : `Not in the directory: ${unresolved.map((k) => k.slice(k.indexOf(':') + 1)).join(', ')}. Untick them to save.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { editing, to, cc, ...fields } = form;
      // No To → leave to/cc out entirely, so the server keeps the recipient role (the pilot inbox).
      const recipients = to.length ? { to: to.map(keyRef), cc: cc.filter((k) => !to.includes(k)).map(keyRef) } : {};
      await call({
        action: 'upsert_definition',
        definition: { ...fields, dueHours: Number(fields.dueHours), ...recipients },
      });
      closeForm();
      await load();
    } catch (err) {
      setError(err?.message || 'Could not save the playbook.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (ev) => setForm((f) => ({ ...f, [key]: ev.target.value })),
  });

  const toggleRef = (list, key) => setForm((f) => {
    const next = f[list].includes(key) ? f[list].filter((k) => k !== key) : [...f[list], key];
    // Clearing the last To also clears Cc: a disabled Cc must not keep a hidden selection that would ride
    // along when a new To is ticked (CodeRabbit on #465).
    if (list === 'to' && !next.length) return { ...f, to: next, cc: [] };
    return { ...f, [list]: next };
  });
  // The directory's recipient people and groups, plus anything the form already names that the directory
  // no longer offers (so it can be seen and unticked, never silently kept).
  const options = () => {
    const opts = [
      ...book.groups.map((g) => ({ key: `group:${g.id}`, label: `${g.name || g.id} (group)` })),
      ...book.people.map((p) => ({ key: `person:${p.id}`, label: p.name || p.id })),
    ];
    for (const k of [...(form?.to || []), ...(form?.cc || [])]) {
      // When the directory failed to load, a saved reference is only "saved" — not known to be missing.
      if (!opts.some((o) => o.key === k)) opts.push({ key: k, unresolved: true, label: `${k.slice(k.indexOf(':') + 1)} (${book.note ? 'saved' : 'not in directory'})` });
    }
    return opts;
  };
  const picker = (list, title, disabled) => (
    <fieldset className="sm:col-span-2" aria-label={title} disabled={disabled}>
      <legend className={labelCls}>{title}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
        {options().map((o) => (
          <label key={o.key} className="inline-flex items-center gap-1">
            <input type="checkbox" aria-label={`${title}: ${o.label}`} checked={form[list].includes(o.key)} onChange={() => toggleRef(list, o.key)} />
            {o.label}
          </label>
        ))}
        {!options().length && <span className="text-carbon-gray-60">No recipient people or groups in the directory yet.</span>}
      </div>
    </fieldset>
  );
  const recipientText = (d) => (Array.isArray(d.to) && d.to.length
    ? d.to.map((r) => (r.group ? `${r.group} (group)` : r.person)).join(', ')
    : (d.recipient || '—'));

  return (
    <section className="border border-carbon-border bg-carbon-white" data-testid="playbooks-panel" aria-label="Playbooks">
      <div className="flex items-center justify-between border-b border-carbon-border bg-carbon-gray-10 px-4 py-2">
        <span className="font-plex-mono text-[11px] uppercase tracking-[0.12em] text-carbon-gray-60">
          Playbooks{open ? ` · ${definitions.length}` : ''}
        </span>
        <button type="button" onClick={toggle} className={btnCls}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <div className="space-y-3 px-4 py-3">
          {error && <div role="alert" className="text-sm text-carbon-red-60">{error}</div>}
          {loading && <div className="text-sm text-carbon-gray-60">Loading playbooks…</div>}
          {!loading && (
            <table className="w-full text-left text-[12px]" data-testid="playbooks-table">
              <thead>
                <tr className="text-carbon-gray-60">
                  <th className="py-1 font-normal">Name</th><th className="font-normal">Rule</th>
                  <th className="font-normal">Email</th><th className="font-normal">To</th><th className="font-normal">Reply within</th>
                  <th className="font-normal">Version</th><th className="font-normal">Managed by</th><th />
                </tr>
              </thead>
              <tbody>
                {definitions.map((d) => (
                  <tr key={d.id} data-testid={`playbook-${d.id}`} className="border-t border-carbon-border">
                    <td className="py-1 text-carbon-ink">{d.name}<span className="ml-2 font-plex-mono text-carbon-gray-50">{d.id}</span></td>
                    <td className="font-plex-mono">{d.rule}</td>
                    <td className="font-plex-mono">{d.template}</td>
                    <td>{recipientText(d)}</td>
                    <td>{d.dueHours != null ? `${d.dueHours}h` : '—'}</td>
                    <td>{d.version ?? '—'}</td>
                    <td>{d.managedBy || '—'}</td>
                    <td className="text-right"><button type="button" className={btnCls} onClick={() => edit(d)} disabled={saving}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!form && (
            <button type="button" className={btnCls} onClick={() => openForm({ ...EMPTY, template: templates[0] || '', editing: false })} disabled={loading}>
              New playbook
            </button>
          )}
          {form && (
            <form onSubmit={save} className="grid grid-cols-1 gap-2 border border-carbon-border p-3 sm:grid-cols-2" data-testid="playbook-form">
              <label><span className={labelCls}>Id</span><input className={inputCls} aria-label="Id" {...field('id')} disabled={form.editing} required /></label>
              <label><span className={labelCls}>Name</span><input className={inputCls} aria-label="Name" {...field('name')} required /></label>
              <label><span className={labelCls}>Finding rule</span><input className={inputCls} aria-label="Finding rule" {...field('rule')} disabled={form.editing} required /></label>
              <label>
                <span className={labelCls}>Email</span>
                <select className={inputCls} aria-label="Email" {...field('template')} required>
                  {templates.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label><span className={labelCls}>Subject</span><input className={inputCls} aria-label="Subject" {...field('subject')} required /></label>
              <label><span className={labelCls}>Reply within (hours)</span><input className={inputCls} aria-label="Reply within (hours)" type="number" min="1" step="1" {...field('dueHours')} required /></label>
              {picker('to', 'To', false)}
              {picker('cc', 'Cc', !form.to.length)}
              {book.note && <p className="text-[11px] text-carbon-red-60 sm:col-span-2" data-testid="directory-note">{book.note}</p>}
              <p className="text-[11px] text-carbon-gray-60 sm:col-span-2">
                {form.to.length
                  ? 'Sent to the people and groups ticked above, looked up in the directory when the email goes out.'
                  : 'No To ticked: sent to the vendor role. During the pilot that is your own inbox.'}
                {' '}One playbook per finding rule; a playbook's rule can't be changed once saved.
              </p>
              <div className="flex gap-4 sm:col-span-2">
                <button type="submit" className={btnCls} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button type="button" className={btnCls} onClick={closeForm} disabled={saving}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
